import React from "react";
import StatWrapper from "../shared/StatWrapper";
import PieChart from "../shared/PieChart";
import Heading from "hardcover-ui/components/Heading";
import Text from "hardcover-ui/components/Text";
import pluralize from "lib/pluralize";
import { useChartTheme } from "../contexts/ChartThemeContext";
import { usePage } from "@inertiajs/react";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";
import LocaleNumber from "components/ui/LocaleNumber";

interface ReadingFormatStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

const formatLabel = (label: string, count: number) => {
  if (label === "Ebooks") return "ebooks";
  if (label === "Audiobooks") return "audiobooks";
  if (label === "Physical Books") return "physical books";
  return `${label} ${pluralize("book", count)}`;
};

const ReadingFormatStat: React.FC<ReadingFormatStatProps> = ({ size }) => {
  const { stats } = usePage<UserStatsPageProps>().props;
  const { readingFormatBreakdown: data } = stats;
  const { getTheme, getMonotheme } = useChartTheme();

  if (!data || data.length === 0) {
    return (
      <StatWrapper title="Reading Format" size={size}>
        <Heading size="sm" className="mb-4">
          No reading format data available
        </Heading>
      </StatWrapper>
    );
  }

  const colors = [
    {
      color: getTheme(5),
      mono: getMonotheme(1),
    },
    {
      color: getTheme(4),
      mono: getMonotheme(5),
    },
    {
      color: getMonotheme(7),
      mono: getMonotheme(10),
    },
  ];

  const totalBooks = data.reduce((sum, item) => sum + (item.value || 0), 0);
  const formatsWithColors = data
    .filter((format) => (format.value || 0) > 0)
    .map((format, index) => ({
      ...format,
      value: format.value || 0,
      color: colors[index]?.color || colors[0].color,
    }));

  const mainFormat = data
    .slice()
    .sort((a, b) => (b.value || 0) - (a.value || 0))[0];

  if (!mainFormat) {
    return (
      <StatWrapper title="Reading Format" size={size}>
        <Heading size="sm" className="mb-4">
          No reading format data available
        </Heading>
      </StatWrapper>
    );
  }

  return (
    <StatWrapper title="Reading Format" size={size}>
      <div className="flex flex-col justify-between h-full w-full">
        <Heading size="sm" className="mb-4">
          Your preferred format is{" "}
          {formatLabel(mainFormat.label, mainFormat.value || 0)}.
        </Heading>
        <div className="flex flex-col justify-between items-center h-full">
          <PieChart data={formatsWithColors} size={200} />
          <div className="mt-4 w-full">
            {formatsWithColors.map((item) => (
              <div
                key={item.label}
                className="flex justify-between items-center mb-2"
              >
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <Text className="capitalize">
                    {formatLabel(item.label, item.value)}
                  </Text>
                </div>
                <Text>
                  {totalBooks > 0
                      ? Math.round(((item.value || 0) / totalBooks) * 100)
                      : 0}
                  % (<LocaleNumber number={item.value || 0} />{" "}
                  {pluralize("book", item.value || 0)})
                </Text>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StatWrapper>
  );
};

export default ReadingFormatStat;
